import { useInView } from "@/hooks/useInView";

const Footer = () => {
  const { ref: footerRef, isInView: footerInView } = useInView({ threshold: 0.1 });

  return (
    <footer id="footer" className="bg-foreground text-white section-padding">
      <div className="container-wide">
        <div
          ref={footerRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {/* Company Info */}
          <div
            className={`space-y-4 transition-all duration-1000 delay-100 ${
              footerInView
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            <h3 className="text-2xl font-bold font-heading text-primary-glow">
              Revive Meal Ethiopia
            </h3>
            <p className="font-body text-gray-300 leading-relaxed">
              Transforming Ethiopian health through fresh, personalized meal
              prep and delivery. Your journey to wellness starts here.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/revivemeals.et"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity cursor-pointer"
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="2"
                    y="2"
                    width="20"
                    height="20"
                    rx="5"
                    ry="5"
                    fill="url(#instagram-gradient)"
                  />
                  <path
                    d="M16 11.37A4.63 4.63 0 1 1 11.37 7 4.63 4.63 0 0 1 16 11.37Z"
                    stroke="white"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  <line
                    x1="17.5"
                    y1="6.5"
                    x2="17.51"
                    y2="6.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient
                      id="instagram-gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#833ab4" />
                      <stop offset="50%" stopColor="#fd1d1d" />
                      <stop offset="100%" stopColor="#fcb045" />
                    </linearGradient>
                  </defs>
                </svg>
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div
            className={`space-y-4 transition-all duration-1000 delay-700 ${
              footerInView
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <h4 className="text-lg font-semibold font-heading text-white">
              Contact Us
            </h4>
            <div className="space-y-3 font-body text-gray-300">
              <div className="flex items-start space-x-3">
                <span className="text-primary mt-1">📍</span>
                <div>
                  <p className="font-medium text-white">Main Office</p>
                  <p className="text-sm">Megenagna, Addis Ababa</p>
                  <p className="text-sm">Marathon building</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <span className="text-primary">📞</span>
                <div>
                  <p className="font-medium text-white">Phone</p>
                  <div>
                    <a
                      href="tel:+251940759311"
                      className="text-sm hover:text-primary transition-colors cursor-pointer"
                    >
                      +251 94 075 9311
                    </a>
                  </div>
                  <div>
                    <a
                      href="tel:+251946329744"
                      className="text-sm hover:text-primary transition-colors cursor-pointer"
                    >
                      +251 94 632 9744
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <span className="text-primary">✉️</span>
                <div>
                  <p className="font-medium text-white">Email</p>
                  <a
                    href="mailto:revivemeals2025@gmail.com"
                    className="text-sm hover:text-primary transition-colors cursor-pointer"
                  >
                    revivemeals2025@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div
            className={`space-y-4 transition-all duration-1000 delay-300 ${
              footerInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <h4 className="text-lg font-semibold font-heading text-white">
              Quick Links
            </h4>
            <nav className="space-y-2 font-body">
              <a
                href="#about"
                className="block text-gray-300 hover:text-primary transition-colors"
              >
                About Us
              </a>
              <a
                href="#pricing"
                className="block text-gray-300 hover:text-primary transition-colors"
              >
                Pricing Plans
              </a>
              <a
                href="#registration"
                className="block text-gray-300 hover:text-primary transition-colors"
              >
                Register Now
              </a>
              <a
                href="#"
                className="block text-gray-300 hover:text-primary transition-colors"
              >
                FAQs
              </a>
              <a
                href="#"
                className="block text-gray-300 hover:text-primary transition-colors"
              >
                Terms of Service
              </a>
            </nav>
          </div>

          {/* Business Hours */}
          <div
            className={`space-y-4 transition-all duration-1000 delay-500 ${
              footerInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-10"
            }`}
          >
            <h4 className="text-lg font-semibold font-heading text-white">
              Business Hours
            </h4>
            <div className="space-y-2 font-body text-gray-300">
              <div className="flex justify-between">
                <span>Monday - Friday</span>
                <span className="text-white">9:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday</span>
                <span className="text-white">9:00 AM - 5:00 PM</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday</span>
                <span className="text-white">9:00 AM - 5:00 PM</span>
              </div>
            </div>

            <div className="bg-primary/20 rounded-xl p-4 mt-4">
              <p className="text-sm font-body text-gray-300">
                <span className="text-primary font-medium">
                  🚚 Delivery Hours:
                </span>
                <br />
                Daily 8:00 AM - 9:00 PM
                <br />
                Free delivery within Addis Ababa
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="font-body text-gray-300 text-sm">
              © {new Date().getFullYear()} Revive Meal Ethiopia. All rights
              reserved.
            </p>
            <div className="flex space-x-6 text-sm font-body">
              <a
                href="#"
                className="text-gray-300 hover:text-primary transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className="text-gray-300 hover:text-primary transition-colors"
              >
                Terms of Use
              </a>
              <a
                href="#"
                className="text-gray-300 hover:text-primary transition-colors"
              >
                Cookie Policy
              </a>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="font-body text-gray-400 text-xs">
              Made with ❤️ for the health and wellness of Ethiopia
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
